'use client';

import { FC, useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';

interface ProductItem {
  icon: FC<{ className?: string }>;
  name: string;
  description: string;
  href: string;
}

const VisitorIdIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z" stroke="currentColor" strokeWidth="1.5" />
    <path d="M17 17.5c0-2.485-2.239-4.5-5-4.5s-5 2.015-5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SALES_PRODUCTS: ProductItem[] = [
  {
    icon: ({ className }) => (
      <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    name: 'Daily SDR Playbook',
    description: 'AI-prioritized daily task list',
    href: '/products/daily-playbook',
  },
  {
    icon: VisitorIdIcon,
    name: 'Website Visitor ID',
    description: 'See who\'s on your site',
    href: '/products/visitor-identification',
  },
  {
    icon: ({ className }) => (
      <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 9L12 12.5L17 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="7" y="8" width="10" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
    name: 'Email Automation',
    description: 'Scale personalized outreach',
    href: '/products/emails',
  },
  {
    icon: ({ className }) => (
      <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M16 15.25C16 15.44 15.96 15.635 15.875 15.82C15.79 16.005 15.68 16.18 15.535 16.345C15.285 16.62 15.01 16.82 14.7 16.95C14.395 17.08 14.06 17.145 13.695 17.145C13.17 17.145 12.61 17.015 12.015 16.75C11.42 16.485 10.825 16.135 10.23 15.7C9.63 15.26 9.06 14.775 8.52 14.24C7.985 13.7 7.5 13.13 7.065 12.535C6.635 11.94 6.285 11.345 6.025 10.755C5.765 10.16 5.635 9.6 5.635 9.075C5.635 8.72 5.695 8.385 5.815 8.08C5.935 7.77 6.13 7.49 6.395 7.24C6.72 6.92 7.075 6.76 7.45 6.76C7.595 6.76 7.74 6.79 7.87 6.85C8.005 6.91 8.125 7 8.22 7.135L9.37 8.745C9.465 8.875 9.535 8.995 9.58 9.105C9.625 9.21 9.65 9.315 9.65 9.41C9.65 9.53 9.615 9.65 9.545 9.765C9.48 9.88 9.385 10 9.265 10.12L8.865 10.535C8.805 10.595 8.775 10.665 8.775 10.755C8.775 10.8 8.78 10.84 8.79 10.885C8.805 10.93 8.82 10.965 8.83 11C8.925 11.18 9.09 11.41 9.325 11.69C9.565 11.97 9.82 12.255 10.1 12.54C10.39 12.825 10.67 13.085 10.955 13.32C11.235 13.55 11.465 13.715 11.65 13.81C11.68 13.82 11.715 13.835 11.755 13.85C11.8 13.865 11.845 13.87 11.895 13.87C11.99 13.87 12.06 13.835 12.12 13.775L12.52 13.385C12.645 13.26 12.765 13.165 12.88 13.105C12.995 13.035 13.11 13 13.235 13C13.33 13 13.43 13.02 13.54 13.065C13.65 13.11 13.765 13.175 13.895 13.265L15.525 14.43C15.66 14.525 15.75 14.635 15.805 14.765C15.855 14.895 15.885 15.025 15.885 15.17"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    name: 'Smart Dialer',
    description: 'Browser dialer for warm outbound',
    href: '/products/cold-calls',
  },
  {
    icon: ({ className }) => (
      <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        <path d="M12 3v2M12 19v2M3 12h2M19 12h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    name: 'Champion Tracking',
    description: 'Track job changes',
    href: '/products/champion-tracking',
  },
];

const MARKETING_PRODUCTS: ProductItem[] = [
  {
    icon: ({ className }) => (
      <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 7C9.239 7 7 9.239 7 12C7 13.193 7.434 14.285 8.159 15.131L7.538 16.616C7.449 16.831 7.566 17.076 7.789 17.146L9.631 17.766C10.345 18.238 11.193 18.5 12.086 18.5C14.762 18.5 17 16.262 17 13.586C17 10.824 14.762 8.5 12 8.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="12" r="0.75" fill="currentColor" />
        <circle cx="12" cy="12" r="0.75" fill="currentColor" />
        <circle cx="14" cy="12" r="0.75" fill="currentColor" />
      </svg>
    ),
    name: 'AI Chatbot',
    description: 'Engage visitors and capture leads',
    href: '/products/ai-chatbot',
  },
  {
    icon: ({ className }) => (
      <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M20 20l-4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M8.5 11h5M11 8.5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    name: 'AI SEO',
    description: 'See how AI talks about your brand',
    href: '/products/ai-seo',
  },
  {
    icon: VisitorIdIcon,
    name: 'Website Visitor ID',
    description: 'See who\'s on your site',
    href: '/products/visitor-identification',
  },
];

const ProductsDropdown: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle mouse enter with delay
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 150); // 150ms delay before opening
  };

  // Handle mouse leave with delay
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms delay before closing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(!isOpen);
    }
  };

  const renderProductLink = (product: ProductItem, index: number) => (
    <Link
      key={index}
      href={product.href}
      className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors duration-150"
      role="menuitem"
      onClick={() => setIsOpen(false)}
    >
      <div className="flex-shrink-0 w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600">
        <product.icon className="w-[18px] h-[18px]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-black">{product.name}</p>
        <p className="text-xs text-slate-500 mt-0.5">{product.description}</p>
      </div>
    </Link>
  );

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger */}
      <button
        className="inline-flex items-center gap-1 h-6 leading-6 text-base cursor-pointer text-black hover:text-[#0848E7] transition-colors duration-200 align-middle p-0"
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        Products
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[640px] bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-[99999]"
          role="menu"
        >
          <div className="grid grid-cols-2 gap-x-4">
            {/* Left column: Sales */}
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2.5 mb-1">Sales</p>
              {SALES_PRODUCTS.map(renderProductLink)}
            </div>

            {/* Right column: Marketing */}
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2.5 mb-1">Marketing</p>
              {MARKETING_PRODUCTS.map(renderProductLink)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsDropdown;
